import React from 'react';
import { Link } from 'react-router-dom';
import HelpApiLayout from '../../../components/layout/HelpApiLayout';
import ApiSidebar from '../../../components/layout/ApiSidebar';
import { PUBLIC_ROUTES } from '../../../constants/routes';
import { getApiDocsRoute } from '../../../utils/navigation';
import './ApiDocsPage.css';

/**
 * API Documentation Overview Page
 * Introduction and getting started guide for the API
 */
const Overview = () => {
  return (
    <HelpApiLayout type="api" sidebar={<ApiSidebar />}>
      <div className="api-docs-page">
        <div className="api-docs-page-container">
          <main className="api-docs-main">
            <header className="api-docs-page-header">
              <h1 className="lp-section-title">API Overview</h1>
              <p className="lp-section-subtitle">
                Welcome to the WeSplit API documentation. This guide will help you integrate
                WeSplit functionality into your application.
              </p>
            </header>

            <section className="api-docs-page-section">
              <h2>Getting Started</h2>
              <p>
                The WeSplit API allows you to programmatically manage splits, transactions,
                wallets, and more. All API requests must be authenticated using API keys.
              </p>
            </section>

            <section className="api-docs-page-section">
              <h2>Base URL</h2>
              <p>All API requests should be made to:</p>
              <code className="api-base-url">https://us-central1-wesplit-35186.cloudfunctions.net</code>
              <p className="api-base-url-note">
                <small>Firebase Cloud Functions v1 endpoints deployed in us-central1 region</small>
              </p>
            </section>

            <section className="api-docs-page-section">
              <h2>Authentication</h2>
              <p>
                API requests require authentication via API keys. Include your API key in
                the request headers. See the{' '}
                <Link to={getApiDocsRoute(PUBLIC_ROUTES.API_DOCS_AUTH)}>Authentication</Link> section for details.
              </p>
            </section>

            <section className="api-docs-page-section">
              <h2>Rate Limits</h2>
              <p>
                API requests are rate-limited to ensure fair usage. Current limits are:
              </p>
              <ul>
                <li>100 requests per 15 minutes per API key</li>
                <li>Additional global rate limits may apply</li>
              </ul>
              <p>
                Rate limit information is included in response headers: <code>X-RateLimit-Limit</code> and <code>X-RateLimit-Remaining</code>
              </p>
            </section>

            <section className="api-docs-page-section">
              <h2>Response Format</h2>
              <p>
                All API responses are returned in JSON format. Successful responses include
                the requested data, while errors include an error code and message.
              </p>
            </section>

            <section className="api-docs-page-section">
              <h2>Available Endpoints</h2>
              <p>Quick reference for all external partner integration endpoints:</p>
              <div className="endpoints-quick-reference">
                <table className="endpoints-table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Endpoint</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span className="method-badge method-post">POST</span></td>
                      <td><code>/createSplitFromPayment</code></td>
                      <td>Create a split from external payment/order data</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-post">POST</span></td>
                      <td><code>/inviteParticipantsToSplit</code></td>
                      <td>Invite participants to an existing split</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-post">POST</span></td>
                      <td><code>/payParticipantShare</code></td>
                      <td>Record a participant's payment for their share</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-get">GET</span></td>
                      <td><code>/getSplitStatus</code></td>
                      <td>Get split status and payment progress</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-post">POST</span></td>
                      <td><code>/batchInviteParticipants</code></td>
                      <td>Batch invite multiple participants with email notifications</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-get">GET</span></td>
                      <td><code>/searchKnownUsers</code></td>
                      <td>Search for existing WeSplit users</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-post">POST</span></td>
                      <td><code>/matchUsersByEmail</code></td>
                      <td>Cross-reference emails with WeSplit database</td>
                    </tr>
                    <tr>
                      <td><span className="method-badge method-post">POST</span></td>
                      <td><code>/spendWebhook</code></td>
                      <td>Receive webhooks from external partners</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ marginTop: '16px' }}>
                For detailed documentation on each endpoint, see the{' '}
                <Link to={getApiDocsRoute(PUBLIC_ROUTES.API_DOCS_SPLITS)}>Splits API</Link> section.
              </p>
            </section>
          </main>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default Overview;
